/**
 * Static-page schemas for the CMS "Pages" tab.
 *
 * Each schema declares which blocks make up a page and which fields each
 * block exposes to the editor. The backend stores the resulting JSON blob
 * untouched (sanitising rich-text body fields). Frontend pages read the
 * blob via `usePage(slug)` and merge each block over their hardcoded
 * defaults so unedited pages keep their original copy.
 *
 * Block types:
 *   hero_slide   → image + title (bilingual) + subtitle (bilingual) + link
 *   card         → image + title (bilingual) + description (bilingual textarea) + link
 *   text         → title (bilingual) + body (bilingual rich text)
 *   image        → image only (hero/banner)
 *   cta          → label (bilingual) + link
 */

export const PAGE_SCHEMAS = {
  home: {
    slug: 'home',
    label_en: 'Home',
    label_gr: 'Αρχική',
    description_en: 'Hero slides + featured cards on the homepage.',
    description_gr: 'Slides ήρωα + προβεβλημένες κάρτες στην αρχική σελίδα.',
    blocks: [
      {
        key: 'hero1',
        type: 'hero_slide',
        label_en: 'Hero — Slide 1',
        label_gr: 'Hero — Σλάιντ 1',
        defaults: {
          link: '/expeditions',
          title_en: 'Mountain Climbing Adventures',
          title_gr: 'Περιπέτειες Ορειβασίας',
          subtitle_en: 'Discover the peaks of the world',
          subtitle_gr: 'Ανακάλυψε τις κορυφές του κόσμου',
        },
      },
      {
        key: 'hero2',
        type: 'hero_slide',
        label_en: 'Hero — Slide 2',
        label_gr: 'Hero — Σλάιντ 2',
        defaults: {
          link: '/classes',
          title_en: 'Climbing Courses',
          title_gr: 'Σχολές Αναρρίχησης',
          subtitle_en: 'Master the vertical world',
          subtitle_gr: 'Κατακτήστε τον κάθετο κόσμο',
        },
      },
      {
        key: 'hero3',
        type: 'hero_slide',
        label_en: 'Hero — Slide 3',
        label_gr: 'Hero — Σλάιντ 3',
        defaults: {
          link: '/expeditions?category=Hymettus',
          title_en: 'Hiking Experiences',
          title_gr: 'Εμπειρίες Πεζοπορίας',
          subtitle_en: 'Explore the paths of Hymettus',
          subtitle_gr: 'Εξερεύνησε τα μονοπάτια του Υμηττού',
        },
      },
      {
        key: 'feat1',
        type: 'card',
        label_en: 'Featured Card 1',
        label_gr: 'Προβεβλημένη Κάρτα 1',
        defaults: {
          link: '/hymettus',
          title_en: 'Local Trail Network',
          title_gr: 'Τοπικό δίκτυο μονοπατιών',
          description_en:
            'Explore our carefully mapped local hiking trails. From beginner-friendly paths to challenging routes, find the adventure that fits you.',
          description_gr:
            'Εξερευνήστε τα προσεκτικά χαρτογραφημένα τοπικά μονοπάτια πεζοπορίας μας. Από φιλικά προς αρχάριους μονοπάτια έως απαιτητικές διαδρομές.',
        },
      },
      {
        key: 'feat2',
        type: 'card',
        label_en: 'Featured Card 2',
        label_gr: 'Προβεβλημένη Κάρτα 2',
        defaults: {
          link: '/expeditions',
          title_en: '2025-2026 Ascent Programme',
          title_gr: 'Πρόγραμμα Αναβάσεων 2025-2026',
          description_en:
            'Join us on unforgettable journeys to mountain peaks and scenic valleys. Activities for every skill level all year long.',
          description_gr:
            'Ελάτε μαζί μας σε αξέχαστα ταξίδια σε ορεινές κορυφές και γραφικές κοιλάδες. Δραστηριότητες για κάθε επίπεδο όλο τον χρόνο.',
        },
      },
    ],
  },

  hymettus_trails: {
    slug: 'hymettus_trails',
    label_en: 'Hymettus — Trails',
    label_gr: 'Υμηττός — Μονοπάτια',
    description_en: 'Hero banner + intro text on the /hymettus page.',
    description_gr: 'Banner ήρωα + εισαγωγικό κείμενο στη σελίδα /hymettus.',
    blocks: [
      {
        key: 'hero',
        type: 'image',
        label_en: 'Hero image',
        label_gr: 'Εικόνα Hero',
      },
      {
        key: 'intro',
        type: 'text',
        label_en: 'Intro section',
        label_gr: 'Εισαγωγική ενότητα',
        rows: 6,
      },
      {
        key: 'cta',
        type: 'cta',
        label_en: 'Primary call-to-action',
        label_gr: 'Κύριο call-to-action',
        defaults: { link: '/expeditions?category=Hymettus' },
      },
    ],
  },

  hymettus_climbing: {
    slug: 'hymettus_climbing',
    label_en: 'Hymettus — Climbing',
    label_gr: 'Υμηττός — Αναρρίχηση',
    description_en: 'Hero banner + intro + downloadable PDF guides on /hymettus/climbing.',
    description_gr: 'Banner ήρωα + εισαγωγή + οδηγοί PDF στη σελίδα /hymettus/climbing.',
    blocks: [
      {
        key: 'hero',
        type: 'image',
        label_en: 'Hero image',
        label_gr: 'Εικόνα Hero',
      },
      {
        key: 'intro',
        type: 'text',
        label_en: 'Intro section',
        label_gr: 'Εισαγωγική ενότητα',
        rows: 6,
      },
      {
        key: 'guides',
        type: 'pdf_list',
        label_en: 'Downloadable PDF guides',
        label_gr: 'Οδηγοί PDF προς λήψη',
        defaults: {
          items: [
            {
              title_en: 'Climbing Sectors of Ilioupoli & Karea',
              title_gr: 'Αναρριχητικά πεδία Ηλιούπολης & Καρέα',
              description_en: '93 routes across 6 sectors — full topo guide refurbished by SPAY (Oct–Dec 2017).',
              description_gr: '93 διαδρομές σε 6 πεδία — πλήρης οδηγός topo, ανακατασκευή ΣΠΑΥ (Οκτ–Δεκ 2017).',
              file_url: '/pdfs/anarrichitika-pedia-ilioupolis-karea.pdf',
            },
            {
              title_en: 'Difficulty Grades — Hymettus Crags',
              title_gr: 'Βαθμολογία αναρριχητικών πεδίων Υμηττού',
              description_en: 'Per-route difficulty grades and historical first-ascent log.',
              description_gr: 'Βαθμολογία ανά διαδρομή και ιστορικές πρωτοαναβάσεις.',
              file_url: '/pdfs/vathmologia-anarrichitika-pedia.pdf',
            },
          ],
        },
      },
      {
        key: 'equipment',
        type: 'text',
        label_en: 'Equipment guide',
        label_gr: 'Οδηγός εξοπλισμού',
        rows: 8,
      },
    ],
  },

  difficulty_grades: {
    slug: 'difficulty_grades',
    label_en: 'Difficulty Grades',
    label_gr: 'Βαθμοί Δυσκολίας',
    description_en: 'Intro text + footnote on /usefull/difficulty-grades.',
    description_gr: 'Εισαγωγικό κείμενο + υποσημείωση στη σελίδα /usefull/difficulty-grades.',
    blocks: [
      {
        key: 'intro',
        type: 'text',
        label_en: 'Intro / overview',
        label_gr: 'Εισαγωγή / επισκόπηση',
        rows: 5,
      },
      {
        key: 'footnote',
        type: 'text',
        label_en: 'Footnote / source',
        label_gr: 'Υποσημείωση / πηγή',
        rows: 3,
      },
    ],
  },

  who_we_are: {
    slug: 'who_we_are',
    label_en: 'Who we are',
    label_gr: 'Ποιοι είμαστε',
    description_en: 'Hero banner + tagline on the /about page.',
    description_gr: 'Banner ήρωα + tagline στη σελίδα /about.',
    blocks: [
      {
        key: 'hero',
        type: 'image',
        label_en: 'Hero image',
        label_gr: 'Εικόνα Hero',
      },
      {
        key: 'tagline',
        type: 'text',
        label_en: 'Hero tagline',
        label_gr: 'Tagline ήρωα',
        rows: 3,
        rich: false,
      },
    ],
  },

  athletes: {
    slug: 'athletes',
    label_en: 'Athletes',
    label_gr: 'Αθλητές',
    description_en: 'Public page describing the Rock-Climbing Athletes program (hero + 5 content blocks + paperwork list).',
    description_gr: 'Δημόσια σελίδα για την ομάδα αθλητικής αναρρίχησης (hero + 5 ενότητες + δικαιολογητικά).',
    blocks: [
      {
        key: 'hero',
        type: 'hero_slide',
        label_en: 'Hero',
        label_gr: 'Hero',
        defaults: {
          image_url: 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1920&h=900&fit=crop&q=80',
          title_en: 'Rock-Climbing Athletes',
          title_gr: 'Αθλητές Αναρρίχησης',
          subtitle_en: 'Train hard. Compete proudly. Represent E.O.S. Ilioupolis.',
          subtitle_gr: 'Σκληρή προπόνηση. Περήφανες συμμετοχές. Εκπροσωπούμε τον Ε.Ο.Σ. Ηλιούπολης.',
        },
      },
      {
        key: 'about',
        type: 'text',
        label_en: 'What it\u2019s about',
        label_gr: 'Τι ακριβώς είναι',
        rich: true,
        rows: 8,
        defaults: {
          title_en: 'About the program',
          title_gr: 'Σχετικά με την ομάδα',
          body_en: 'Our athletics team brings together members who want to compete in sport-climbing (lead, boulder & speed) at national level. Athletes train weekly with a dedicated coach paid by the club, follow a structured calendar of EOOA-sanctioned championships, and represent E.O.S. Ilioupolis at podiums across Greece.',
          body_gr: 'Η αγωνιστική μας ομάδα συγκεντρώνει μέλη που θέλουν να αγωνιστούν στην αθλητική αναρρίχηση (δυσκολία, boulder & ταχύτητα) σε εθνικό επίπεδο. Οι αθλητές προπονούνται εβδομαδιαία με προπονητή που πληρώνει ο σύλλογος, ακολουθούν δομημένο πρόγραμμα πανελλήνιων αγώνων υπό την αιγίδα της ΕΟΟΑ και εκπροσωπούν τον Ε.Ο.Σ. Ηλιούπολης σε βάθρα σε όλη την Ελλάδα.',
        },
      },
      {
        key: 'how_to_join',
        type: 'text',
        label_en: 'How to become an athlete',
        label_gr: 'Πώς γίνεσαι αθλητής',
        rich: true,
        rows: 8,
        defaults: {
          title_en: 'Joining the team',
          title_gr: 'Πώς μπαίνεις στην ομάδα',
          body_en: '1. Be an active club member in good standing.\n2. Demonstrate consistent climbing ability at a minimum agreed grade.\n3. Attend a 2-week trial training block with the coach.\n4. Commit to the weekly training schedule and championship calendar.\n5. Submit the EOOA athlete-registration paperwork (see below).',
          body_gr: '1. Να είσαι ενεργό μέλος του συλλόγου, σε τάξη.\n2. Σταθερή αναρριχητική επίδοση σε συμφωνημένη ελάχιστη βαθμολογία.\n3. Συμμετοχή σε διήμερο/εβδομαδιαίο τεστ προπόνησης με τον προπονητή.\n4. Δέσμευση στο εβδομαδιαίο πρόγραμμα και στο αγωνιστικό ημερολόγιο.\n5. Κατάθεση δικαιολογητικών ΕΟΟΑ (βλέπε παρακάτω).',
        },
      },
      {
        key: 'privileges',
        type: 'text',
        label_en: 'Privileges',
        label_gr: 'Προνόμια',
        rich: true,
        rows: 8,
        defaults: {
          title_en: 'What the club gives back',
          title_gr: 'Τι παρέχει ο σύλλογος',
          body_en: '• Coach paid by the club (weekly training)\n• Championship registration fees covered\n• Travel & accommodation for sanctioned events covered\n• Use of club climbing gear & facilities for training\n• Recognition on the public Honor Wall\n• Priority entry to club training blocks',
          body_gr: '• Προπονητής με κάλυψη μισθού από τον σύλλογο (εβδομαδιαία προπόνηση)\n• Κάλυψη παραβόλων συμμετοχής στους αγώνες\n• Κάλυψη μετακινήσεων & διαμονής σε επίσημες διοργανώσεις\n• Χρήση εξοπλισμού και εγκαταστάσεων του συλλόγου για προπόνηση\n• Αναγνώριση στον δημόσιο Τοίχο Τιμής\n• Προτεραιότητα σε προπονητικά blocks του συλλόγου',
        },
      },
      {
        key: 'obligations',
        type: 'text',
        label_en: 'Obligations',
        label_gr: 'Υποχρεώσεις',
        rich: true,
        rows: 8,
        defaults: {
          title_en: 'What we expect',
          title_gr: 'Τι περιμένουμε',
          body_en: '• Attend at least 75% of weekly training sessions\n• Participate in the championships agreed at season start\n• Keep your EOOA athlete card and medical clearance up to date\n• Represent the club with sportsmanship & respect\n• Notify the coach of injuries / absences promptly',
          body_gr: '• Παρουσία τουλάχιστον 75% στις εβδομαδιαίες προπονήσεις\n• Συμμετοχή στους συμφωνημένους αγώνες της σεζόν\n• Διατήρηση της κάρτας αθλητή ΕΟΟΑ και της ιατρικής βεβαίωσης σε ισχύ\n• Εκπροσώπηση του συλλόγου με ευγένεια & σεβασμό\n• Άμεση ενημέρωση του προπονητή για τραυματισμούς / απουσίες',
        },
      },
      {
        key: 'paperwork',
        type: 'text',
        label_en: 'Necessary paperwork (EOOA card)',
        label_gr: 'Δικαιολογητικά (Κάρτα Αθλητή ΕΟΟΑ)',
        rich: true,
        rows: 8,
        defaults: {
          title_en: 'EOOA athlete-card registration',
          title_gr: 'Έκδοση Κάρτας Αθλητή ΕΟΟΑ',
          body_en: 'To get an EOOA athlete card you need:\n• Recent passport-style photo\n• Photocopy of ID card (both sides)\n• Recent medical clearance (cardiologist) — no older than 6 months\n• Signed athlete application form (we provide it)\n• Athlete-registration fee (paid via the club to EOOA)\n• For minors: parental consent form & legal-guardian ID copy\n\nSubmit everything to athletes@eosh.gr or hand it to the secretariat. We file with EOOA on your behalf.',
          body_gr: 'Για την έκδοση Κάρτας Αθλητή ΕΟΟΑ χρειάζονται:\n• Πρόσφατη φωτογραφία τύπου ταυτότητας\n• Φωτοτυπία ταυτότητας (μπρος-πίσω)\n• Πρόσφατη ιατρική βεβαίωση (καρδιολόγος) — έως 6 μήνες\n• Υπογεγραμμένη αίτηση αθλητή (την παρέχουμε)\n• Παράβολο εγγραφής αθλητή (καταβάλλεται μέσω συλλόγου στην ΕΟΟΑ)\n• Για ανηλίκους: υπεύθυνη δήλωση γονέα & φωτοτυπία ταυτότητας κηδεμόνα\n\nΣτείλε τα στο athletes@eosh.gr ή παρέδωσέ τα στη γραμματεία. Καταθέτουμε στην ΕΟΟΑ εκ μέρους σου.',
        },
      },
    ],
  },
};

export const PAGE_SLUGS = Object.keys(PAGE_SCHEMAS);

/** Convenience: read a single block from a page's `blocks` map, falling
 *  back to the schema's `defaults` for missing fields. */
export const readBlock = (pageBlocks, slug, blockKey, lang = 'en') => {
  const schema = PAGE_SCHEMAS[slug];
  if (!schema) return {};
  const blockSchema = schema.blocks.find((b) => b.key === blockKey);
  if (!blockSchema) return {};
  const defaults = blockSchema.defaults || {};
  const stored = (pageBlocks && pageBlocks[blockKey]) || {};
  const merged = { ...defaults, ...stored };
  const isGr = lang === 'gr';
  // Convenience localised aliases
  if (merged.title_en !== undefined || merged.title_gr !== undefined) {
    merged.title = isGr ? (merged.title_gr || merged.title_en || '') : (merged.title_en || merged.title_gr || '');
  }
  if (merged.subtitle_en !== undefined || merged.subtitle_gr !== undefined) {
    merged.subtitle = isGr ? (merged.subtitle_gr || merged.subtitle_en || '') : (merged.subtitle_en || merged.subtitle_gr || '');
  }
  if (merged.description_en !== undefined || merged.description_gr !== undefined) {
    merged.description = isGr
      ? (merged.description_gr || merged.description_en || '')
      : (merged.description_en || merged.description_gr || '');
  }
  if (merged.body_en !== undefined || merged.body_gr !== undefined) {
    merged.body = isGr ? (merged.body_gr || merged.body_en || '') : (merged.body_en || merged.body_gr || '');
  }
  if (merged.label_en !== undefined || merged.label_gr !== undefined) {
    merged.label = isGr ? (merged.label_gr || merged.label_en || '') : (merged.label_en || merged.label_gr || '');
  }
  // pdf_list: localise each item's title/description
  if (Array.isArray(merged.items)) {
    merged.items = merged.items
      .filter((it) => it && (it.title_en || it.title_gr || it.file_url))
      .map((it) => ({
        ...it,
        title: isGr ? (it.title_gr || it.title_en || '') : (it.title_en || it.title_gr || ''),
        description: isGr
          ? (it.description_gr || it.description_en || '')
          : (it.description_en || it.description_gr || ''),
      }));
  }
  return merged;
};
;
